"use client"

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from "react"
import { useNavigate } from "react-router-dom"
import { DEMO_STEPS, type AgentDemoStep } from "@/lib/agent-demo-data"
import { DEMO_SCENARIO_LIST, DEMO_STORAGE_KEY, loadScenarioSteps } from "@/lib/demo-scenarios"

/* ── Context shape ── */
interface DemoContextValue {
  /** Static list of available demo scenarios */
  scenarioList: typeof DEMO_SCENARIO_LIST
  /** Currently selected scenario ID (persisted to localStorage) */
  selectedScenarioId: string
  /** Step array for the current scenario */
  demoSteps: AgentDemoStep[]
  /** Load a new scenario */
  selectScenario: (id: string) => void
  /** Auto-fill text from the first user step of the current scenario */
  scenarioPrefill: string

  /** Current/last chat query */
  chatQuery: string
  /** Counter to force remount of chat session */
  chatSessionKey: number
  /** Set query + navigate to /chat */
  sendChat: (query: string) => void
}

const DemoContext = createContext<DemoContextValue | null>(null)

export function useDemoContext() {
  const ctx = useContext(DemoContext)
  if (!ctx) throw new Error("useDemoContext must be used within DemoProvider")
  return ctx
}

export function DemoProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate()

  const [selectedScenarioId, setSelectedScenarioId] = useState("default")
  const [demoSteps, setDemoSteps] = useState<AgentDemoStep[]>(DEMO_STEPS)
  const [scenarioPrefill, setScenarioPrefill] = useState("")
  const [chatQuery, setChatQuery] = useState("")
  const [chatSessionKey, setChatSessionKey] = useState(0)

  /* ── Init: load saved scenario from localStorage ── */
  useEffect(() => {
    const saved = localStorage.getItem(DEMO_STORAGE_KEY)
    const scenarioToLoad = saved || "default"
    if (saved) setSelectedScenarioId(saved)
    loadScenarioSteps(scenarioToLoad).then((steps) => {
      setDemoSteps(steps)
      const firstUserStep = steps.find((s) => s.type === "user")
      if (firstUserStep?.content) {
        setScenarioPrefill(firstUserStep.content)
        // If already on /chat (page refresh), auto-start the demo with the saved scenario
        if (window.location.pathname === "/chat") {
          setChatQuery(firstUserStep.content)
          setChatSessionKey((k) => k + 1)
        }
      }
    })
  }, [])

  const selectScenario = useCallback((id: string) => {
    setSelectedScenarioId(id)
    localStorage.setItem(DEMO_STORAGE_KEY, id)
    loadScenarioSteps(id).then((steps) => {
      setDemoSteps(steps)
      const firstUserStep = steps.find((s) => s.type === "user")
      if (firstUserStep?.content) setScenarioPrefill(firstUserStep.content)
    })
  }, [])

  const sendChat = useCallback(
    (query: string) => {
      setChatQuery(query)
      setChatSessionKey((k) => k + 1)
      navigate("/chat")
    },
    [navigate]
  )

  return (
    <DemoContext.Provider
      value={{
        scenarioList: DEMO_SCENARIO_LIST,
        selectedScenarioId,
        demoSteps,
        selectScenario,
        scenarioPrefill,
        chatQuery,
        chatSessionKey,
        sendChat,
      }}
    >
      {children}
    </DemoContext.Provider>
  )
}
